import React from 'react';
import { formatDimensionWithUnits, parseDimensionWithUnits } from './dimensionUtils';

/**
 * Parameters needed for dimension event handlers
 */
export interface DimensionEventHandlerParams {
  dimensions: { x: number; y: number; z: number } | null;
  useInches: boolean;
  setEditingDimensions: React.Dispatch<React.SetStateAction<Record<'x' | 'y' | 'z', string | null>>>;
  handleScale: (axis: 'x' | 'y' | 'z', newValue: number) => void;
}

/**
 * Creates dimension event handlers with the provided parameters
 */
export const createDimensionEventHandlers = (params: DimensionEventHandlerParams) => {
  const { dimensions, useInches, setEditingDimensions, handleScale } = params;

  /**
   * Toggle between mm and inches
   */
  const toggleUnits = (setUseInches: React.Dispatch<React.SetStateAction<boolean>>) => {
    setUseInches(prev => !prev);
  };

  /**
   * Handle dimension input focus (when user clicks on input)
   */
  const handleDimensionFocus = (axis: 'x' | 'y' | 'z', event: React.FocusEvent<HTMLInputElement>) => {
    if (!dimensions) return;
    
    // Set the current formatted value as the editing value
    const currentValue = formatDimensionWithUnits(dimensions[axis], useInches);
    setEditingDimensions(prev => ({
      ...prev,
      [axis]: currentValue
    }));
    
    // Select all text when focused to make it easy to replace
    setTimeout(() => {
      event.target.select();
    }, 0);
  };

  /**
   * Handle dimension input change (while typing)
   */
  const handleDimensionChange = (axis: 'x' | 'y' | 'z', value: string) => {
    // Allow any input while editing (including empty string)
    setEditingDimensions(prev => ({
      ...prev,
      [axis]: value
    }));
  };

  /**
   * Handle dimension input blur (when user clicks away) or Enter key
   */
  const handleDimensionSubmit = (axis: 'x' | 'y' | 'z', editingDimensions: Record<'x' | 'y' | 'z', string | null>) => {
    const editingValue = editingDimensions[axis];
    
    if (editingValue === null) return; // Not currently editing
    
    // Try to parse the value
    const newValue = parseDimensionWithUnits(editingValue, useInches);
    
    if (!isNaN(newValue) && newValue > 0) {
      // Valid value - apply the scale
      handleScale(axis, newValue);
    }
    
    // Clear the editing state
    setEditingDimensions(prev => ({
      ...prev,
      [axis]: null
    }));
  };

  /**
   * Handle key press in dimension input
   */
  const handleDimensionKeyPress = (
    axis: 'x' | 'y' | 'z', 
    e: React.KeyboardEvent,
    editingDimensions: Record<'x' | 'y' | 'z', string | null>
  ) => {
    if (e.key === 'Enter') {
      handleDimensionSubmit(axis, editingDimensions);
      // Blur the input to remove focus
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      // Cancel editing
      setEditingDimensions(prev => ({
        ...prev,
        [axis]: null
      }));
      (e.target as HTMLInputElement).blur();
    }
  };

  return {
    toggleUnits,
    handleDimensionFocus,
    handleDimensionChange,
    handleDimensionSubmit,
    handleDimensionKeyPress
  };
}; 